import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, downloadFile } from '../lib/api-client';
import { useT } from '../lib/i18n';
import type { TenderStatus, TenderSummary } from '../lib/types';

type TenderRow = TenderSummary & { deleted_at: number | null; archived_at?: number | null };

type BatchResponse = {
  action: string;
  total: number;
  ok: number;
  failed: number;
  results: Array<{ id: string; ok: boolean; error?: string }>;
};

type ListResponse = {
  items: TenderRow[];
  limit: number;
  offset: number;
  total: number;
};

export function Dashboard() {
  const { t } = useT();
  const STATUS_FILTERS: Array<{ value: TenderStatus | ''; label: string }> = [
    { value: '', label: t('dashboard.filter.all') },
    { value: 'ready', label: t('dashboard.filter.ready') },
    { value: 'reviewed', label: t('dashboard.filter.reviewed') },
    { value: 'exported', label: t('dashboard.filter.exported') },
    { value: 'extracting', label: t('dashboard.filter.processing') },
    { value: 'failed', label: t('dashboard.filter.failed') },
  ];

  const [tenders, setTenders] = useState<TenderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TenderStatus | ''>('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchNote, setBatchNote] = useState<string | null>(null);

  function fetchList() {
    setLoading(true);
    setSelected(new Set());
    const params = new URLSearchParams();
    if (filter) params.set('statut', filter);
    if (q) params.set('q', q);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (showDeleted) params.set('onlyDeleted', 'true');
    if (showArchived) params.set('onlyArchived', 'true');
    const qs = params.toString();
    api
      .get<ListResponse>(`/api/tenders${qs ? `?${qs}` : ''}`)
      .then((d) => {
        setTenders(d.items ?? []);
        setTotal(d.total ?? d.items?.length ?? 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(fetchList, [filter, showDeleted, showArchived]);

  // Lignes sélectionnables (pas en corbeille) + gestion de la sélection.
  const selectableIds = useMemo(
    () => tenders.filter((t) => !t.deleted_at).map((t) => t.id),
    [tenders],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  }

  async function runBatch(action: 'process' | 'delete' | 'archive' | 'unarchive' | 'export') {
    const ids = [...selected];
    if (ids.length === 0) return;
    const labels: Record<string, string> = {
      process: 'Lancer le traitement', delete: 'Supprimer', archive: 'Archiver',
      unarchive: 'Désarchiver', export: 'Exporter',
    };
    if (action === 'delete' && !window.confirm(`Supprimer ${ids.length} AO ?`)) return;
    setBatchBusy(true);
    setBatchNote(null);
    setError(null);
    try {
      if (action === 'export') {
        // Export client : un download par AO prêt (ready/reviewed/exported).
        const exportable = tenders.filter((t) => selected.has(t.id) && ['ready', 'reviewed', 'exported'].includes(t.statut));
        let done = 0;
        for (const t of exportable) {
          try {
            await downloadFile(`/api/tenders/${t.id}/download`, `${t.nom || t.id}.xlsx`);
            done += 1;
          } catch {
            /* ignore individual export errors */
          }
        }
        setBatchNote(`${labels[action]} : ${done}/${ids.length} exporté(s)${exportable.length < ids.length ? ` (${ids.length - exportable.length} non prêts ignorés)` : ''}`);
      } else {
        const res = await api.post<BatchResponse>('/api/tenders/batch', { action, ids });
        setBatchNote(`${labels[action]} : ${res.ok}/${res.total} OK${res.failed ? `, ${res.failed} échec(s)` : ''}`);
        fetchList();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur action groupée');
    } finally {
      setBatchBusy(false);
    }
  }

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    fetchList();
  }

  async function restore(id: string) {
    try {
      await api.post(`/api/tenders/${id}/restore`);
      fetchList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  const kpis = useMemo(() => computeKpis(tenders), [tenders]);

  return (
    <div>
      {/* Page header */}
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          <div className="tc-label mb-2">tenders / overview</div>
          <h1 className="text-2xl font-display tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-sm text-steel-500 mt-1 font-mono">
            {total} record{total !== 1 ? 's' : ''}
            {total > tenders.length && ` · ${tenders.length} shown`}
          </p>
        </div>
        <Link to="/upload" data-onboarding-target="upload-link" className="tc-btn-primary">
          + {t('dashboard.new_tender')}
        </Link>
      </div>

      {/* KPIs grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-700 mb-8 border border-ink-700">
        <KpiCard label={t('dashboard.kpi.this_month')} value={kpis.thisMonthCount} />
        <KpiCard
          label={t('dashboard.kpi.precision')}
          value={kpis.precisionPct != null ? `${kpis.precisionPct}%` : '—'}
        />
        <KpiCard
          label={t('dashboard.kpi.total_ht')}
          value={kpis.totalHt > 0 ? `${(kpis.totalHt / 1000).toFixed(1)}k €` : '—'}
        />
        <KpiCard
          label={t('dashboard.kpi.to_review')}
          value={kpis.toReviewCount}
          highlight={kpis.toReviewCount > 0}
        />
      </div>

      {/* Search bar */}
      <form onSubmit={applySearch} className="tc-panel p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-6">
            <label className="tc-label block mb-1.5" htmlFor="search-q">
              {t('dashboard.search.placeholder')}
            </label>
            <input
              id="search-q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="rechercher des AO..."
              className="tc-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="tc-label block mb-1.5" htmlFor="search-from">FROM</label>
            <input
              id="search-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="tc-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="tc-label block mb-1.5" htmlFor="search-to">TO</label>
            <input
              id="search-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="tc-input"
            />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="tc-btn-secondary flex-1">
              SEARCH
            </button>
            {(q || from || to) && (
              <button
                type="button"
                onClick={() => {
                  setQ('');
                  setFrom('');
                  setTo('');
                  fetchList();
                }}
                className="tc-btn-ghost"
                aria-label="Reset"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Filtres */}
      <div className="flex gap-1 mb-4 items-center overflow-x-auto pb-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setFilter(f.value);
              setShowDeleted(false);
              setShowArchived(false);
            }}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
              filter === f.value && !showDeleted && !showArchived
                ? 'bg-amber-500 text-ink-950'
                : 'bg-ink-900 text-steel-300 hover:text-steel-100 border border-ink-700'
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => {
            setShowArchived(!showArchived);
            setShowDeleted(false);
            setFilter('');
          }}
          className={`ml-auto px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
            showArchived
              ? 'bg-sky-900/40 text-sky-300 border border-sky-900/60'
              : 'bg-ink-900 text-steel-300 hover:text-steel-100 border border-ink-700'
          }`}
        >
          archives
        </button>
        <button
          onClick={() => {
            setShowDeleted(!showDeleted);
            setShowArchived(false);
            setFilter('');
          }}
          className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
            showDeleted
              ? 'bg-red-900/40 text-red-300 border border-red-900/60'
              : 'bg-ink-900 text-steel-300 hover:text-steel-100 border border-ink-700'
          }`}
        >
          corbeille
        </button>
      </div>

      {/* Barre d'actions groupées */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 border border-amber-900/50 bg-amber-900/10 px-3 py-2">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-300">
            {selected.size} sélectionné{selected.size > 1 ? 's' : ''}
          </span>
          <div className="flex flex-wrap gap-2 ml-auto">
            {showArchived ? (
              <button onClick={() => runBatch('unarchive')} disabled={batchBusy} className="tc-btn-secondary text-xs">
                Désarchiver
              </button>
            ) : (
              <>
                <button onClick={() => runBatch('process')} disabled={batchBusy} className="tc-btn-primary text-xs">
                  Traiter / Retraiter
                </button>
                <button onClick={() => runBatch('export')} disabled={batchBusy} className="tc-btn-secondary text-xs">
                  Exporter Excel
                </button>
                <button onClick={() => runBatch('archive')} disabled={batchBusy} className="tc-btn-secondary text-xs">
                  Archiver
                </button>
              </>
            )}
            <button onClick={() => runBatch('delete')} disabled={batchBusy} className="tc-btn-ghost text-xs text-red-300">
              Supprimer
            </button>
            <button onClick={() => setSelected(new Set())} disabled={batchBusy} className="tc-btn-ghost text-xs">
              Annuler
            </button>
          </div>
        </div>
      )}
      {batchNote && (
        <div className="border border-ink-700 bg-ink-900/60 text-steel-300 px-4 py-2 mb-4 text-xs font-mono">
          {batchNote}
        </div>
      )}

      {error && (
        <div className="border border-red-900/60 bg-red-900/20 text-red-300 px-4 py-3 mb-4 text-sm font-mono">
          ERROR · {error}
        </div>
      )}

      {loading ? (
        <div className="tc-panel p-8 text-center text-steel-500 font-mono text-sm">
          Chargement...
        </div>
      ) : tenders.length === 0 ? (
        <div className="tc-panel p-12 text-center text-steel-500">
          <div className="font-mono text-xs uppercase tracking-widest mb-2">aucun AO</div>
          <div className="text-sm">
            {showDeleted
              ? 'La corbeille est vide.'
              : filter || q || from || to
                ? 'Aucun AO ne correspond à ces filtres.'
                : 'Aucun AO traité pour le moment.'}
          </div>
        </div>
      ) : (
        <div className="tc-panel overflow-x-auto">
          <table className="min-w-[900px]">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Tout sélectionner"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={selectableIds.length === 0}
                  />
                </th>
                <th>NAME</th>
                <th>CLIENT</th>
                <th>STATUS</th>
                <th className="text-right">LINES</th>
                <th className="text-right">TOTAL HT</th>
                <th>H / M / L</th>
                <th>RECEIVED</th>
                {showDeleted && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tenders.map((t) => (
                <tr key={t.id} className={t.deleted_at ? 'opacity-50' : selected.has(t.id) ? 'bg-amber-900/10' : ''}>
                  <td className="w-8">
                    {!t.deleted_at && (
                      <input
                        type="checkbox"
                        aria-label={`Sélectionner ${t.nom}`}
                        checked={selected.has(t.id)}
                        onChange={() => toggleOne(t.id)}
                      />
                    )}
                  </td>
                  <td>
                    {t.deleted_at ? (
                      <span className="text-steel-500 line-through">{t.nom}</span>
                    ) : (
                      <Link to={`/tenders/${t.id}`} className="text-amber-500 hover:text-amber-400">
                        {t.nom}
                      </Link>
                    )}
                  </td>
                  <td className="text-steel-300">{t.client_final ?? '—'}</td>
                  <td><StatusBadge statut={t.statut} /></td>
                  <td className="font-mono text-right text-steel-300">{t.nb_lignes ?? '—'}</td>
                  <td className="font-mono text-right text-steel-100">
                    {t.total_ht != null ? `${t.total_ht.toLocaleString('fr-FR')} €` : '—'}
                  </td>
                  <td className="font-mono text-2xs">
                    <span className="text-emerald-400">{t.nb_matches_high ?? 0}</span>
                    <span className="text-steel-500"> · </span>
                    <span className="text-amber-400">{t.nb_matches_medium ?? 0}</span>
                    <span className="text-steel-500"> · </span>
                    <span className="text-red-400">{t.nb_matches_low ?? 0}</span>
                  </td>
                  <td className="font-mono text-2xs text-steel-500">
                    {new Date(t.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  {showDeleted && (
                    <td>
                      <button onClick={() => restore(t.id)} className="tc-btn-ghost text-xs">
                        RESTORE
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function computeKpis(tenders: TenderRow[]) {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const thisMonth = tenders.filter((t) => t.created_at >= startOfMonth);
  const totalHigh = tenders.reduce((s, t) => s + (t.nb_matches_high ?? 0), 0);
  const totalMatched = tenders.reduce(
    (s, t) => s + (t.nb_matches_high ?? 0) + (t.nb_matches_medium ?? 0) + (t.nb_matches_low ?? 0),
    0,
  );
  const precisionPct = totalMatched > 0 ? Math.round((totalHigh / totalMatched) * 100) : null;
  return {
    thisMonthCount: thisMonth.length,
    precisionPct,
    totalHt: tenders.reduce((s, t) => s + (t.total_ht ?? 0), 0),
    toReviewCount: tenders.filter((t) => t.statut === 'ready').length,
  };
}

function KpiCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className={`bg-ink-900 p-5 ${highlight ? 'border-l-2 border-amber-500' : ''}`}>
      <div className="tc-label">{label}</div>
      <div className="text-3xl font-display tracking-tight mt-2 text-steel-100">{value}</div>
    </div>
  );
}

function StatusBadge({ statut }: { statut: TenderStatus }) {
  const map: Record<TenderStatus, { cls: string; code: string }> = {
    uploaded:   { cls: 'tc-status-neutral', code: 'UPL' },
    extracting: { cls: 'tc-status-warn',    code: 'EXT' },
    text_extracted: { cls: 'tc-status-warn', code: 'TXT' },
    awaiting_params_review: { cls: 'tc-status-warn', code: 'PAR' },
    awaiting_references_review: { cls: 'tc-status-warn', code: 'REF' },
    awaiting_final_review: { cls: 'tc-status-warn', code: 'FIN' },
    matching:   { cls: 'tc-status-warn',    code: 'MAT' },
    reviewing:  { cls: 'tc-status-warn',    code: 'REV' },
    generating_excel: { cls: 'tc-status-warn', code: 'XLS' },
    ready:      { cls: 'tc-status-warn',    code: 'RDY' },
    reviewed:   { cls: 'tc-status-ok',      code: 'OK ' },
    exported:   { cls: 'tc-status-ok',      code: 'EXP' },
    retrying:   { cls: 'tc-status-warn',    code: 'RTY' },
    failed:     { cls: 'tc-status-error',   code: 'ERR' },
  };
  const { cls, code } = map[statut];
  return <span className={`tc-status ${cls}`}>{code}</span>;
}
